import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getSchoolBySchoolID } from "../../../services/operations/schoolAPI";
import { getSubscriptionById } from "../../../services/operations/subscriptionsAPI";
import { getUserById } from "../../../services/operations/authAPI";

const SchoolDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [school, setSchool] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [activatedByName, setActivatedByName] = useState("");
  const [userNames, setUserNames] = useState({
    admins: [],
    teachers: [],
    parents: [],
    students: [],
  });

  const [showAll, setShowAll] = useState({
    admins: false,
    teachers: false,
    parents: false,
    students: false,
  });

  // Resolve user name by ID
  const resolveName = async (value) => {
    try {
      if (!value) return "Unknown";
      if (typeof value === "object" && value.name) return value.name;
      const res = await dispatch(getUserById(value));
      return res?.name || "Unknown";
    } catch {
      return "Unknown";
    }
  };

  const fetchNamesArray = async (ids) => {
    return Promise.all(
      (ids || []).map(async (id) => {
        return await resolveName(id);
      })
    );
  };

  useEffect(() => {
    const fetchSchoolData = async () => {
      try {
        const res = await getSchoolBySchoolID(id);
        //console.log("res.",res)
        setSchool(res);

        if (res?.activeSubscriptionId) {
          const subRes = await getSubscriptionById(res.activeSubscriptionId);
          setSubscription(subRes);
          const actName = await resolveName(subRes?.activatedBy);
          setActivatedByName(actName);
        }

        const admins = await fetchNamesArray(res.admins);
        const teachers = await fetchNamesArray(res.teachers);
        const parents = await fetchNamesArray(res.parents);
        const students = await fetchNamesArray(res.students);

        setUserNames({ admins, teachers, parents, students });
      } catch (err) {
        console.error("FETCH SCHOOL ERROR:", err);
      }
    };

    fetchSchoolData();
  }, [id, dispatch]);

  if (!school)
    return (
      <div className="flex items-center justify-center h-screen">
        Loading...
      </div>
    );

  const renderUserList = (list, type) => {
    const limit = 5;
    const show = showAll[type];
    const displayed = show ? list : list.slice(0, limit);

    return (
      <div>
        {displayed.length ? displayed.join(", ") : "-"}
        {list.length > limit && (
          <button
            className="ml-2 text-blue-500 underline"
            onClick={() => setShowAll({ ...showAll, [type]: !show })}
          >
            {show ? "See Less" : "See More"}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-[#00254D]">{school.name}</h1>
        <button
          onClick={() => navigate("/dashboard/super-admin/schools")}
          className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg shadow-md"
        >
          Back to Schools
        </button>
      </div>

      {/* School Info */}
      <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200 space-y-2">
        <p>
          <span className="font-semibold">Name: </span>
          {school.name}
        </p>
        <p>
          <span className="font-semibold">Email: </span>
          {school.email}
        </p>
        <p>
          <span className="font-semibold">Address: </span>
          {school.address}
        </p>
        <p>
          <span className="font-semibold">Subscription Status: </span>
          <span
            className={`px-2 py-1 rounded-full text-sm font-semibold ${
              school.subscriptionStatus === "active"
                ? "bg-green-100 text-green-700"
                : school.subscriptionStatus === "expired"
                ? "bg-red-100 text-red-700"
                : "bg-gray-200 text-gray-600"
            }`}
          >
            {school.subscriptionStatus}
          </span>
        </p>

        {subscription ? (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-1">
            <p>
              <span className="font-semibold">Plan: </span>
              {subscription.plan}
            </p>
            <p>
              <span className="font-semibold">Start Date: </span>
              {new Date(subscription.startDate).toLocaleDateString()}
            </p>
            <p>
              <span className="font-semibold">End Date: </span>
              {new Date(subscription.endDate).toLocaleDateString()}
            </p>
            <p>
              <span className="font-semibold">Active Status: </span>
              {subscription.isActive ? "Active" : "Expired"}
            </p>
            <p>
              <span className="font-semibold">Activated By: </span>
              {activatedByName}
            </p>
            <p>
              <span className="font-semibold">API Limit per Day: </span>
              {subscription.apiLimitPerDay}
            </p>
            <p>
              <span className="font-semibold">API Calls Today: </span>
              {subscription.apiCallsToday}
            </p>
          </div>
        ) : (
          <p className="text-gray-500">No subscription available</p>
        )}

        <p>
          <span className="font-semibold">Created At: </span>
          {new Date(school.createdAt).toLocaleString()}
        </p>
        <p>
          <span className="font-semibold">Updated At: </span>
          {new Date(school.updatedAt).toLocaleString()}
        </p>
      </div>

      {/* Users Info */}
      <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200 space-y-4">
        <h2 className="text-xl font-semibold">
          Users(
          {userNames.admins.length +
            userNames.teachers.length +
            userNames.parents.length +
            userNames.students.length}
          )
        </h2>
        <p>
          <span className="font-semibold">
            Admins:({userNames.admins.length}){" "}
          </span>
          {renderUserList(userNames.admins, "admins")}
        </p>
        <p>
          <span className="font-semibold">
            Teachers:({userNames.teachers.length}){" "}
          </span>
          {renderUserList(userNames.teachers, "teachers")}
        </p>
        <p>
          <span className="font-semibold">
            Parents:({userNames.parents.length}){" "}
          </span>
          {renderUserList(userNames.parents, "parents")}
        </p>
        <p>
          <span className="font-semibold">
            Students:({userNames.students.length}){" "}
          </span>
          {renderUserList(userNames.students, "students")}
        </p>
      </div>
    </div>
  );
};

export default SchoolDetails;
